import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/constants";

const links = [
  { href: "#servicios", label: "Servicios", num: "01" },
  { href: "#filosofia", label: "Filosofía 4R", num: "02" },
  { href: "#nosotros", label: "Nosotros", num: "03" },
  { href: "#cumplimiento", label: "Habilitaciones", num: "04" },
  { href: "#faq", label: "Preguntas", num: "05" },
  { href: "#contacto", label: "Contacto", num: "06" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-asphalt/95 backdrop-blur-md border-b border-forest-700" : "bg-asphalt"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-6">
        <a
          href="#top"
          data-testid="logo-link"
          className="flex items-center gap-3 group shrink-0"
          aria-label="Royal Eco — Inicio"
        >
          <img
            src="/assets/logo.png"
            alt="Royal Eco — Gestión Ambiental"
            className="w-11 h-11 object-contain"
            width="44"
            height="44"
          />
          <div className="leading-none">
            <div className="font-display text-bone text-[26px] md:text-[28px] tracking-tight">
              ROYAL ECO
            </div>
            <div className="tag text-lime-400 mt-1">
              Gestión Ambiental · AR
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Menú principal">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.replace("#", "")}`}
              className="group flex items-baseline gap-1.5 text-bone hover:text-lime-400 transition-colors"
            >
              <span className="tag text-lime-400/60 group-hover:text-lime-400">
                {l.num}
              </span>
              <span className="text-[13px] uppercase tracking-widest2 font-medium">
                {l.label}
              </span>
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center">
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="inline-flex items-center gap-2 bg-lime-400 hover:bg-lime-500 text-asphalt px-5 py-2.5 text-[12px] uppercase tracking-widest2 font-semibold transition-colors"
          >
            <MessageCircle className="w-4 h-4" />
            Cotizar
          </a>
        </div>

        <button
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-bone"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="accent-line w-full" aria-hidden="true" />

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden bg-asphalt border-b border-forest-700"
        >
          <div className="px-5 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                onClick={() => setOpen(false)}
                className="flex items-baseline gap-2 text-bone"
              >
                <span className="tag text-lime-400">{l.num}</span>
                <span className="font-display text-2xl">{l.label}</span>
              </a>
            ))}
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="mobile-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 bg-lime-400 text-asphalt px-5 py-3 text-xs uppercase tracking-widest2 font-semibold mt-2"
            >
              <MessageCircle className="w-4 h-4" />
              Cotizar por WhatsApp
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
